import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivateChild, RouterStateSnapshot } from "@angular/router";
import { buildNewFrontendUrl, mapToNewFrontendPath, resolveMigratedModuleForUrl } from "./front-novo.config";

/**
 * Guard genérico de migração para o front novo. Aplicado via `canActivateChild` no parent de
 * cada módulo migrável, intercepta **qualquer** ativação de rota filha (inclusive navegações
 * internas `router.navigate([...])`). Se o módulo dono do path atual estiver migrado no
 * ambiente ([[isModuleMigrated]]), faz um **full page load** para o front novo preservando o
 * path — quebrando o SPA para que o Front Door (mesma origem) roteie a requisição. Caso
 * contrário, libera a navegação normal no Angular (implementação antiga).
 *
 * O redirect usa `document.location.href` (token `DOCUMENT`, evitando `window` global) porque
 * `redirectTo` do Angular não cobre URL externa. Ver `front-novo.config.ts` para a flag por
 * ambiente e o registro de módulos.
 *
 * **Proteção contra loop (fail-open):** em homolog o `baseUrl` é omitido, então o redirect é
 * relativo (mesma origem) — se o Front Door não interceptar o path por qualquer motivo, o
 * browser recarrega o Angular no **mesmo** `/clientes` e o guard dispararia de novo,
 * infinitamente. Para evitar, um sentinela em `sessionStorage` registra o último alvo
 * redirecionado; num segundo disparo consecutivo para o **mesmo** alvo dentro de
 * {@link REDIRECT_LOOP_WINDOW_MS}, o guard **libera a rota legada** em vez de redirecionar de
 * novo. A janela de tempo torna o sentinela auto-recuperável: quando o Front Door funciona, o
 * front novo assume e a marca velha é ignorada na próxima visita legítima ao path.
 */
@Injectable({ providedIn: "root" })
export class FrontNovoRedirectGuard implements CanActivateChild {
    /** Chave do sentinela anti-loop em `sessionStorage`. */
    private static readonly SENTINEL_KEY = "frontNovoRedirect:last";
    /** Janela (ms) em que um redirect repetido ao mesmo alvo é tratado como loop. */
    private static readonly REDIRECT_LOOP_WINDOW_MS = 5000;

    constructor(@Inject(DOCUMENT) private document: Document) {}

    canActivateChild(_childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
        const moduleKey = resolveMigratedModuleForUrl(state.url);
        if (!moduleKey) {
            return true;
        }

        const target = buildNewFrontendUrl(mapToNewFrontendPath(state.url));
        const storage = this.getSessionStorage();

        if (this.isRedirectLoop(storage, target)) {
            // eslint-disable-next-line no-console
            console.warn("Triggered isRedirectLoop");
            // 2º disparo consecutivo ao mesmo alvo: Front Door provavelmente não interceptou.
            // Fail-open — limpa o sentinela e deixa a rota legada carregar em vez de recarregar.
            this.clearSentinel(storage);
            return true;
        }

        this.markRedirect(storage, target);
        this.document.location.href = target;
        return false; // cancela a navegação interna; o Front Door assume no full page load
    }

    /** `sessionStorage` via `DOCUMENT` (sem `window` global), ou `null` se indisponível. */
    private getSessionStorage(): Storage | null {
        try {
            return this.document.defaultView?.sessionStorage ?? null;
        } catch {
            // Acesso a storage pode lançar (modo privado / storage bloqueado).
            return null;
        }
    }

    /** `true` se já redirecionamos ao **mesmo** `target` há menos de `REDIRECT_LOOP_WINDOW_MS`. */
    private isRedirectLoop(storage: Storage | null, target: string): boolean {
        try {
            const raw = storage?.getItem(FrontNovoRedirectGuard.SENTINEL_KEY);
            if (!raw) {
                return false;
            }
            const last = JSON.parse(raw) as { target?: string; at?: number };
            return (
                last.target === target &&
                typeof last.at === "number" &&
                Date.now() - last.at < FrontNovoRedirectGuard.REDIRECT_LOOP_WINDOW_MS
            );
        } catch {
            // getItem indisponível ou sentinela corrompido: trata como ausente.
            return false;
        }
    }

    /** Grava o alvo e o instante do redirect atual no sentinela. */
    private markRedirect(storage: Storage | null, target: string): void {
        try {
            storage?.setItem(FrontNovoRedirectGuard.SENTINEL_KEY, JSON.stringify({ target, at: Date.now() }));
        } catch {
            // Storage indisponível (quota / política): segue com o redirect sem sentinela.
        }
    }

    /** Limpa o sentinela; ignora falhas de storage para preservar o fail-open. */
    private clearSentinel(storage: Storage | null): void {
        try {
            storage?.removeItem(FrontNovoRedirectGuard.SENTINEL_KEY);
        } catch {
            // Falha ao limpar é inofensiva: a janela de tempo expira o sentinela sozinho.
        }
    }
}
